/**
 * MeetingAiActionStatus — 一项 AI 产出的状态条与触发按钮。
 *
 * 会后总结、待办抽取、会前准备三项共用本组件，各自传入自己的状态：三项的产出状态
 * 互相独立，一项未完成不影响另外两项的呈现与触发。
 *
 * 本组件只负责状态与触发，不负责渲染产出内容。产出内容由调用方从会议本身（摘要、
 * 会前清单）与候选待办列表读取——这样一来，某一次生成未完成时，上一次的产出仍然
 * 照常展示，界面同时给出「上次的结果」与「本次未完成的原因」。
 */

import { Alert, Button, Space, Tag, Tooltip, Typography } from "antd";
import { ReloadOutlined, ThunderboltOutlined } from "@ant-design/icons";
import type { MeetingAiAction, MeetingAnalysisState } from "../../types/meeting";
import {
  MEETING_AI_ACTION_LABELS,
  MEETING_ANALYSIS_STATE_LABELS,
} from "../../types/meeting";

const { Text } = Typography;

const STATE_TAG_COLOR: Record<MeetingAnalysisState, string> = {
  none: "default",
  pending: "processing",
  ready: "green",
  failed: "red",
};

function formatMoment(value: string | null): string | null {
  if (!value) return null;
  const at = new Date(value);
  if (Number.isNaN(at.getTime())) return null;
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${at.getFullYear()}-${pad(at.getMonth() + 1)}-${pad(at.getDate())} ${pad(at.getHours())}:${pad(at.getMinutes())}`;
}

interface MeetingAiActionStatusProps {
  action: MeetingAiAction;
  /** 产出状态。以状态查询的返回为准，它已把失联的「生成中」判为未完成。 */
  state: MeetingAnalysisState;
  /** 本次未完成的原因，面向律师的可读中文。 */
  errorMessage: string | null;
  /** 本次产出完成时刻。 */
  completedAt: string | null;
  /** 界面上是否已有上一次的产出正在展示。决定未完成提示里是否说明结果仍在。 */
  hasPreviousResult: boolean;
  onSubmit: () => void;
  /** 提交请求是否在途。 */
  submitting?: boolean;
  /** 不满足触发条件时置为 true，并给出原因。 */
  blocked?: boolean;
  blockedReason?: string;
}

export default function MeetingAiActionStatus({
  action,
  state,
  errorMessage,
  completedAt,
  hasPreviousResult,
  onSubmit,
  submitting = false,
  blocked = false,
  blockedReason,
}: MeetingAiActionStatusProps) {
  const label = MEETING_AI_ACTION_LABELS[action];
  const running = state === "pending";
  const completedText = formatMoment(completedAt);

  const button = (
    <Button
      type="primary"
      icon={state === "ready" || state === "failed" ? <ReloadOutlined /> : <ThunderboltOutlined />}
      onClick={onSubmit}
      loading={submitting || running}
      disabled={blocked || running}
      data-testid={`meeting-action-submit-${action}`}
    >
      {running ? `${label}生成中` : hasPreviousResult ? `重新生成${label}` : `生成${label}`}
    </Button>
  );

  return (
    <div style={{ marginBottom: "var(--jerry-sp-3)" }}>
      <Space wrap align="center" style={{ marginBottom: "var(--jerry-sp-2)" }}>
        <Tag
          color={STATE_TAG_COLOR[state]}
          data-testid={`meeting-action-state-${action}`}
        >
          {MEETING_ANALYSIS_STATE_LABELS[state]}
        </Tag>
        {blocked && blockedReason ? (
          <Tooltip title={blockedReason}>{button}</Tooltip>
        ) : (
          button
        )}
        {blocked && blockedReason && <Text type="secondary">{blockedReason}</Text>}
        {state === "ready" && completedText && (
          <Text type="secondary">生成于 {completedText}</Text>
        )}
        {running && <Text type="secondary">生成需要一到几分钟，完成后本页自动更新</Text>}
      </Space>
      {state === "failed" && (
        <Alert
          type="error"
          showIcon
          data-testid={`meeting-action-error-${action}`}
          message={`本次${label}未完成`}
          description={
            <span>
              {errorMessage || "未收到具体原因，请重试。"}
              {hasPreviousResult && (
                <>
                  {" "}
                  <Text strong data-testid={`meeting-action-kept-${action}`}>
                    下方仍是上一次生成的结果，未被本次覆盖。
                  </Text>
                </>
              )}
            </span>
          }
        />
      )}
    </div>
  );
}
